"use client";

import React from "react";
import { motion } from "framer-motion";
import { Calculator, Stethoscope, Wrench, Scale, Briefcase, ShoppingBag, Building2, GraduationCap, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
const sectors = [{
  id: "accountants",
  title: "Accountants & Financial Advisors",
  iconName: "calculator",
  description: "Capture high-intent business clients searching for tax planning, advisory, and audit services with structured topical authority.",
  tag1: "High-Ticket Leads",
  tag2: "National Search",
  tag3: "E-E-A-T Strategy"
}, {
  id: "dentists",
  title: "Dentists & Dental Practices",
  iconName: "stethoscope",
  description: "Dominate local map packs and high-margin treatment searches such as Invisalign and implants in your immediate catchment area.",
  tag1: "Google Map Pack",
  tag2: "Patient Acquisition",
  tag3: "Local SEO"
}, {
  id: "trades",
  title: "Plumbers, Electricians & Trades",
  iconName: "wrench",
  description: "Secure immediate local call-outs and high-converting commercial contract inquiries across your target postal codes.",
  tag1: "Emergency Terms",
  tag2: "Geo-Targeting",
  tag3: "Local Reviews"
}, {
  id: "solicitors",
  title: "Solicitors & Law Firms",
  iconName: "scale",
  description: "Outrank aggressive legal competitors for conveyancing, commercial law, and dispute resolution inquiries.",
  tag1: "High-CPC Keywords",
  tag2: "Content Authority",
  tag3: "Practice Visibility"
}, {
  id: "b2b",
  title: "B2B & Professional Services",
  iconName: "briefcase",
  description: "Drive qualified executive decision-makers into your sales pipeline with strategic search funnels and technical SEO.",
  tag1: "Pipeline Growth",
  tag2: "Technical Audits",
  tag3: "B2B Lead Gen"
}, {
  id: "ecommerce",
  title: "E-commerce & Retail Brands",
  iconName: "shoppingbag",
  description: "Scale organic revenue through refined category architecture, product schema, speed optimization, and search-focused copy.",
  tag1: "Schema Markup",
  tag2: "Organic Revenue",
  tag3: "Technical SEO"
}, {
  id: "realestate",
  title: "Property & Real Estate",
  iconName: "building",
  description: "Attract property sellers, landlords, and commercial tenants through optimized localized landing pages and market insights.",
  tag1: "Local Landing Pages",
  tag2: "Valuation Leads",
  tag3: "Search Discovery"
}, {
  id: "education",
  title: "Education & EdTech",
  iconName: "graduation",
  description: "Increase course enrollments and platform registrations through comprehensive organic search discovery and keyword clustering.",
  tag1: "Enrollment Funnels",
  tag2: "Content Clusters",
  tag3: "Brand Growth"
}];
export default function Industry() {
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const renderSectorIcon = (name: string) => {
    switch (name) {
      case "calculator":
        return <Calculator className="size-6 text-primary" />;
      case "stethoscope":
        return <Stethoscope className="size-6 text-primary" />;
      case "wrench":
        return <Wrench className="size-6 text-primary" />;
      case "scale":
        return <Scale className="size-6 text-primary" />;
      case "briefcase":
        return <Briefcase className="size-6 text-primary" />;
      case "shoppingbag":
        return <ShoppingBag className="size-6 text-primary" />;
      case "building":
        return <Building2 className="size-6 text-primary" />;
      case "graduation":
        return <GraduationCap className="size-6 text-primary" />;
      default:
        return <Briefcase className="size-6 text-primary" />;
    }
  };
  return <section id="industrysectorsgrid" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-16 space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary bg-secondary/80 rounded-full border-0">
            Sectors I Serve
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight">
            SEO Strategies Tailored to Your Industry
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            I work directly with practice leaders, business owners, and marketing leads to solve sector-specific search challenges and capture high-converting organic demand.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {sectors.map((sector, i) => <motion.div key={sector.id} data-index={i} variants={itemVariants} className="h-full">
              <Card className="h-full border border-border/80 bg-card hover:bg-card/90 transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-md flex flex-col justify-between">
                <CardHeader className="space-y-3 pb-3">
                  <div className="size-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    {renderSectorIcon(sector.iconName)}
                  </div>
                  <CardTitle className="text-lg font-bold font-serif text-card-foreground">
                    {sector.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 flex-1">
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                    {sector.description}
                  </CardDescription>
                  <div className="flex flex-wrap gap-1.5 pt-2">
                    <Badge variant="outline" className="text-[12.5px] font-medium bg-muted/60 border-border text-foreground/80">
                      {sector.tag1}
                    </Badge>
                    <Badge variant="outline" className="text-[12.5px] font-medium bg-muted/60 border-border text-foreground/80">
                      {sector.tag2}
                    </Badge>
                    <Badge variant="outline" className="text-[12.5px] font-medium bg-muted/60 border-border text-foreground/80">
                      {sector.tag3}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="mt-16 rounded-2xl bg-secondary/40 border border-border p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="space-y-2 max-w-2xl">
            <h3 className="text-xl md:text-2xl font-bold font-serif text-foreground">
              {"Don't see your specific sector listed?"}
            </h3>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
              I adapt proven SEO frameworks, technical audits, and content strategies to any high-competition market or specialized vertical. You always work directly with me.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full md:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/ContactPage">
                Book Free Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/ServicesPage">Explore All Services</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}